import React from "react";
import { X, Download } from "lucide-react";

interface PDFPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  pdfBase64: string | null;
  voucherNumber: string;
  shippingCompany: string;
  loading: boolean;
  error: string | null;
  isPickupList?: boolean;
}

const PDFPreviewModal: React.FC<PDFPreviewModalProps> = ({
  isOpen,
  onClose,
  pdfBase64,
  voucherNumber,
  shippingCompany,
  loading,
  error,
  isPickupList = false,
}) => {
  if (!isOpen) return null;

  const handleDownload = () => {
    if (!pdfBase64) return;

    try {
      // Convert base64 to blob
      const byteCharacters = atob(pdfBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const filename = isPickupList
        ? `${shippingCompany}_pickup_list_${voucherNumber}.pdf`
        : `${shippingCompany}_voucher_${voucherNumber}.pdf`;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading PDF:", error);
      alert("Failed to download PDF. Please try again.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" onClick={onClose}>
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />

        {/* Modal */}
        <div
          className="relative bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[95vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between p-4 border-b border-gray-200"
            style={{ backgroundColor: "#264a3e" }}
          >
            <div className="flex items-center space-x-3">
              <h2 className="text-xl font-semibold text-white">
                {isPickupList
                  ? `Pickup List - ${voucherNumber}`
                  : `Voucher Preview - ${voucherNumber}`}
              </h2>
              <span className="text-sm text-white opacity-75">
                ({shippingCompany.toUpperCase()})
              </span>
            </div>
            <div className="flex items-center space-x-2">
              {pdfBase64 && (
                <button
                  onClick={handleDownload}
                  className="flex items-center space-x-2 px-3 py-2 bg-white text-gray-700 rounded-md hover:bg-gray-100 transition-colors"
                  title="Download PDF"
                >
                  <Download className="w-4 h-4" />
                  <span>Download</span>
                </button>
              )}
              <button
                onClick={onClose}
                className="text-white hover:text-gray-200 transition-colors p-2"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-hidden">
            {loading && (
              <div className="flex items-center justify-center h-full p-4">
                <div
                  className="animate-spin rounded-full h-12 w-12 border-b-2"
                  style={{ borderColor: "#264a3e" }}
                ></div>
              </div>
            )}

            {error && (
              <div className="p-8">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-800 text-sm">{error}</p>
                </div>
              </div>
            )}

            {!loading && !error && pdfBase64 && (
              <div className="h-full">
                <iframe
                  src={`data:application/pdf;base64,${pdfBase64}`}
                  className="w-full h-full"
                  style={{ minHeight: "80vh" }}
                  title="PDF Preview"
                />
              </div>
            )}

            {!loading && !error && !pdfBase64 && (
              <div className="flex items-center justify-center h-full p-8">
                <p className="text-gray-500">No PDF available</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-between items-center p-4 border-t border-gray-200 bg-gray-50">
            <p className="text-xs text-gray-500">
              {isPickupList
                ? `Pickup List: ${voucherNumber}`
                : `Voucher: ${voucherNumber}`}{" "}
              • {shippingCompany.toUpperCase()}
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 text-white rounded-md hover:opacity-90 transition-colors"
              style={{ backgroundColor: "#264a3e" }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PDFPreviewModal;
